import { Anchor, Waves, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDateSafe } from "@/utils/dateHelpers";
import { getCountryFlag } from "@/utils/countryFlags";

/**
 * Dense, read-only row for a single cruise itinerary day.
 *
 * Mirrors the visual rhythm of `renderCollapsedRow` in ItineraryView so the
 * cruise-day rows blend in with surrounding (real) segments. Click → opens
 * the existing CruiseItineraryDialog for editing the day's details.
 *
 * Props:
 *   day               — synthetic day item from cruiseTrips.getCruiseTrips
 *   onOpenItinerary   — callback when the user taps the row; receives the
 *                       parent cruise segment (caller already has it)
 */
export const CruiseDayRow = ({ day, onOpenItinerary }) => {
  const isAtSea = day.at_sea || !day.port_name;
  const Icon = isAtSea ? Waves : Anchor;
  const flag = day.country ? getCountryFlag(day.country) : null;

  // Time line — show whichever of arrival/departure/final-call exist.
  const timeBits = [];
  if (day.arrival_time) timeBits.push(`Arr ${day.arrival_time}`);
  if (day.departure_time) timeBits.push(`Dep ${day.departure_time}`);
  if (!day.arrival_time && !day.departure_time && day.final_call_time) {
    timeBits.push(`Final call ${day.final_call_time}`);
  }
  const timeStr = timeBits.join(" · ");

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-cyan-50/60 dark:hover:bg-cyan-950/30"
      onClick={() => onOpenItinerary?.()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpenItinerary?.();
      }}
      data-testid={`cruise-day-row-${day.id}`}
      title="Click to edit cruise itinerary"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">
            Day {day.day_number}
          </span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
            {isAtSea ? "At Sea" : day.port_name}
          </span>
          {flag && <span className="text-base" aria-hidden="true">{flag}</span>}
        </div>
        {(day.country || day.notes) && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
            {day.country && (
              <>
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span>{day.country}</span>
              </>
            )}
            {day.country && day.notes && <span>·</span>}
            {day.notes && <span className="truncate">{day.notes}</span>}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 text-right">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {formatDateSafe(day.date)}
        </div>
        {timeStr && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            {timeStr}
          </div>
        )}
      </div>
      {isAtSea && (
        <Badge
          variant="outline"
          className="flex-shrink-0 text-[10px] px-1.5 py-0 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800"
        >
          At Sea
        </Badge>
      )}
    </div>
  );
};

export default CruiseDayRow;
